import { defineQueryOptions, useMutation, useQuery, useQueryCache } from "@pinia/colada";
import {
	CreateUser,
	Role,
	UpdateUser,
	useApi,
	User,
	UserApi,
	UserScope,
} from "@soft-db/monitoring-api-client";
import { storeToRefs } from "pinia";
import { useScopeStore } from "../stores/scope";
import { composeCacheEffects, useQueryEnabled } from "./query";

// --- Keys ---
export const usersKeys = {
	root: ["users"] as const,

	list: () => [...usersKeys.root, "list"] as const,

	byContext: () => [...usersKeys.list(), "context"],

	byScope: (scope: UserScope) => [...usersKeys.list(), scope.type, scope.id] as const,

	bySpace: (space: string) => [...usersKeys.list(), "space", space] as const,
	byDist: (dist: string) => [...usersKeys.list(), "dist", dist] as const,
	byId: (id: string) => [...usersKeys.root, "id", id] as const,
};

// --- Cache effects ---

function useUpdateCacheById() {
	const cache = useQueryCache();

	return (user: User) => {
		cache.setQueryData(usersKeys.byId(user.id), user);
	};
}

function useInvalidateListCache() {
	const cache = useQueryCache();

	return () => {
		cache.invalidateQueries({ key: usersKeys.list() });
	};
}

/**
 * Compose both updateCacheById and invalidateListCache effects
 */
function useUserCacheEffects() {
	return composeCacheEffects(useUpdateCacheById(), useInvalidateListCache());
}

// --- Queries ---

export function useUser(id: MaybeRefOrGetter<string>) {
	const api = useApi().user();

	const { data: user, ...rest } = useQuery({
		key: () => usersKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		user,
		data: user,
		...rest,
	};
}

export function useTryUser(id: MaybeRefOrGetter<string>) {
	const api = useApi().user();

	const { data: user, ...rest } = useQuery({
		key: () => usersKeys.byId(toValue(id)),
		query: () => api.tryFetch(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		user,
		...rest,
	};
}

export const userByScopeQuery = defineQueryOptions(
	({ api, scope }: { api: UserApi; scope: UserScope }) => ({
		key: usersKeys.byScope(scope),
		query: async () => {
			switch (scope.type) {
				case "distributor":
					return api.fetchDistUsers(scope.id);
				case "space":
					return api.fetchSpaceUsers(scope.id);
				default:
					return [];
			}
		},
	})
);

// Trying something here...
// It would allow page components to use the same `useUsers` function, but with a different scope as argument.
// It should make it easier to reuse components.
export function useUsers(scope?: MaybeRefOrGetter<UserScope>) {
	const api = useApi().user();

	if (scope == null) {
		scope = storeToRefs(useScopeStore()).scope;
	}

	const queryEnabled = useQueryEnabled();
	const { data: users, ...rest } = useQuery({
		key: () => usersKeys.byScope(toValue(scope)),
		query: async () => {
			const scopeVal = toValue(scope);
			switch (scopeVal.type) {
				case "distributor":
					return api.fetchDistUsers(scopeVal.id);
				case "space":
					return api.fetchSpaceUsers(scopeVal.id);
				default:
					return [];
			}
		},
		enabled: queryEnabled,
	});

	return {
		users,
		data: users,
		...rest,
	};
}

export function useSpaceUsers(spaceId: MaybeRefOrGetter<string>) {
	const api = useApi().user();

	const { data: users, ...rest } = useQuery({
		key: () => usersKeys.bySpace(toValue(spaceId)),
		query: () => api.fetchSpaceUsers(toValue(spaceId)),
		enabled: useQueryEnabled(spaceId),
	});

	return {
		users,
		data: users,
		...rest,
	};
}

export function useDistUsers(distId: MaybeRefOrGetter<string>) {
	const api = useApi().user();

	const { data: users, ...rest } = useQuery({
		key: () => usersKeys.byDist(toValue(distId)),
		query: () => api.fetchDistUsers(toValue(distId)),
		enabled: useQueryEnabled(distId),
	});

	return {
		users,
		data: users,
		...rest,
	};
}

// --- Mutations ---

export function useCreateUser() {
	const api = useApi().user();

	return useMutation({
		mutation: ({ scope, user }: { scope: UserScope; user: CreateUser }) => {
			switch (scope.type) {
				case "distributor":
					return api.createDistUser(scope.id, user);
				case "space":
					return api.createSpaceUser(scope.id, user);
				default:
					throw new Error("invalid scope for user creation");
			}
		},

		onSuccess: useInvalidateListCache(),
	});
}

export function useUpdateUserAvatar() {
	const api = useApi().user();

	return useMutation({
		mutation: ({ id, image }: { id: string; image: File }) => api.updateAvatar(id, image),
		onSuccess: useUserCacheEffects(),
	});
}

export function useDeleteUserAvatar() {
	const api = useApi().user();

	return useMutation({
		mutation: ({ id }: { id: string }) => api.deleteAvatar(id),
		onSuccess: useUserCacheEffects(),
	});
}

export function useUpdateUser() {
	const api = useApi().user();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: UpdateUser }) => api.update(id, data),
		onSuccess: useUserCacheEffects(),
	});
}

export function useUpdateUserEmail() {
	const api = useApi().user();

	return useMutation({
		mutation: ({ id, email }: { id: string; email: string }) => api.updateEmail(id, email),
		onSuccess: useUserCacheEffects(),
	});
}

export function useUpdateUserRole() {
	const api = useApi().user();

	return useMutation({
		mutation: ({ id, role }: { id: string; role: Role }) => api.updateRole(id, role),
		onSuccess: useUserCacheEffects(),
	});
}

export function useResetUserPassword() {
	const api = useApi().auth();

	return useMutation({
		mutation: ({ id, password }: { id: string; password: string }) =>
			api.resetPassword(id, password),
	});
}

export function userResendActivationLink() {
	const api = useApi().user();

	return useMutation({
		mutation: ({ id }: { id: string }) => api.resendActivation(id),
	});
}

export function userResetPasswordUserRequest() {
	const api = useApi().auth();

	return useMutation({
		mutation: ({ email }: { email: string }) => api.resetPasswordRequest(email),
	});
}

export function useArchiveUser() {
	const api = useApi().user();

	// TODO: Fix: Remove user id fetch after delete.
	return useMutation({
		mutation: (id: string) => api.archive(id),
		onSuccess: useInvalidateListCache(),
	});
}

export function useRestoreUser() {
	const api = useApi().user();

	return useMutation({
		mutation: (id: string) => api.restore(id),
		onSuccess: useInvalidateListCache(),
	});
}
